import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Edit } from "lucide-react";
import { toast } from "sonner";
import { StudentInfoCard } from "@/components/student-detail/StudentInfoCard";
import { LessonHistoryCard } from "@/components/student-detail/LessonHistoryCard";
import { ChargesCard } from "@/components/student-detail/ChargesCard";
import { DocumentsCard } from "@/components/student-detail/DocumentsCard";
import { PaymentStatusCard } from "@/components/student-detail/PaymentStatusCard";
import { NotesCard } from "@/components/student-detail/NotesCard";
import { EditStudentDialog } from "@/components/student-detail/EditStudentDialog";
import { PaymentOverrideDialog } from "@/components/student-detail/PaymentOverrideDialog";

export default function StudentDetail() {
  const { studentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [appts, setAppts] = useState([]);
  const [docs, setDocs] = useState([]);
  const [balance, setBalance] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [noteText, setNoteText] = useState("");
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideType, setOverrideType] = useState("courtesy");
  const [overrideNoteText, setOverrideNoteText] = useState("");
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const isStaff = user?.role === "admin" || user?.role === "office_staff";
  const canAddNote = isStaff || user?.role === "instructor";
  const editMeetingLocationOptions = useMemo(
    () => locations.filter((l) => l.active || l.name === editForm.default_meeting_location),
    [locations, editForm.default_meeting_location]
  );

  const load = useCallback(async () => {
    try {
      const promises = [
        api.get(`/students/${studentId}`),
        api.get("/appointments", { params: { student_id: studentId } }),
        api.get("/documents", { params: { student_id: studentId } }),
        api.get(`/students/${studentId}/balance`),
        api.get("/settings/meeting-locations"),
      ];
      if (isStaff) {
        promises.push(api.get("/instructors"));
        promises.push(api.get(`/students/${studentId}/payment-status`));
        promises.push(api.get("/charges", { params: { student_id: studentId } }));
      }
      const results = await Promise.all(promises);
      setStudent(results[0].data);
      setEditForm(results[0].data);
      setAppts(results[1].data);
      setDocs(results[2].data);
      setBalance(results[3].data);
      setLocations(results[4].data);
      if (isStaff) {
        setInstructors(results[5].data);
        setPaymentStatus(results[6].data);
        setCharges(results[7].data);
      }
    } catch {
      toast.error("Could not load student");
    } finally {
      setLoading(false);
    }
  }, [studentId, isStaff]);

  useEffect(() => {
    load();
  }, [load]);

  const saveEdit = async () => {
    try {
      await api.put(`/students/${studentId}`, {
        ...editForm,
        classroom_hours_completed: editForm.classroom_hours_completed !== undefined && editForm.classroom_hours_completed !== null
          ? Number(editForm.classroom_hours_completed) : undefined,
        observation_hours_completed: editForm.observation_hours_completed !== undefined && editForm.observation_hours_completed !== null
          ? Number(editForm.observation_hours_completed) : undefined,
      });
      toast.success("Student updated");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update student");
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/students/${studentId}/notes`, { note: noteText, note_type: user.role });
      setNoteText("");
      toast.success("Note added");
      load();
    } catch {
      toast.error("Could not add note");
    }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("student_id", studentId);
    formData.append("category", "general");
    formData.append("file", file);
    try {
      await api.post("/documents/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      load();
    } catch {
      toast.error("Upload failed");
    }
  };

  const openOverrideDialog = (type) => {
    setOverrideType(type);
    setOverrideNoteText("");
    setOverrideDialogOpen(true);
  };

  const submitOverride = async () => {
    setOverrideSubmitting(true);
    try {
      const res = await api.post(`/students/${studentId}/payment-override`, {
        override_type: overrideType,
        note: overrideNoteText || undefined,
      });
      setPaymentStatus(res.data);
      toast.success(overrideType === "full" ? "Full override granted — hold removed" : "Courtesy override granted — allows one booking");
      setOverrideDialogOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not grant override");
    } finally {
      setOverrideSubmitting(false);
    }
  };

  const revokeOverride = async () => {
    try {
      const res = await api.post(`/students/${studentId}/payment-override/revoke`);
      setPaymentStatus(res.data);
      toast.success("Override revoked");
    } catch {
      toast.error("Could not revoke override");
    }
  };

  const waiveCharge = async (chargeId) => {
    try {
      await api.put(`/charges/${chargeId}/waive`);
      toast.success("Fee waived");
      load();
    } catch {
      toast.error("Could not waive fee");
    }
  };

  if (loading || !student) {
    return <Skeleton className="h-96 rounded-[var(--radius)]" />;
  }

  return (
    <div className="space-y-6" data-testid="student-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="student-detail-back-button">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold">{student.full_name}</h1>
          <p className="text-sm text-muted-foreground">Parent: {student.parent_name} {student.parent_phone && `· ${student.parent_phone}`}</p>
        </div>
        {isStaff && (
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-student-button">
            <Edit className="h-4 w-4 mr-2" /> Edit Details
          </Button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <StudentInfoCard student={student} />
          <LessonHistoryCard appointments={appts} />
          {isStaff && <ChargesCard charges={charges} onWaiveCharge={waiveCharge} />}
          <DocumentsCard documents={docs} onUpload={uploadDoc} />
        </div>

        <div className="space-y-6">
          {balance && (
            <section className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-1">
              <p className="text-sm text-muted-foreground">Outstanding Balance</p>
              <p className={`text-2xl font-semibold font-heading ${balance.balance > 0 ? "text-destructive" : "text-emerald-600"}`}>
                ${balance.balance.toFixed(2)}
              </p>
            </section>
          )}

          {isStaff && paymentStatus && (
            <PaymentStatusCard
              paymentStatus={paymentStatus}
              onOpenOverrideDialog={openOverrideDialog}
              onRevokeOverride={revokeOverride}
            />
          )}

          <NotesCard
            notes={student.notes}
            canAddNote={canAddNote}
            noteText={noteText}
            onNoteTextChange={setNoteText}
            onAddNote={addNote}
          />
        </div>
      </div>

      <EditStudentDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        form={editForm}
        onFormChange={setEditForm}
        meetingLocationOptions={editMeetingLocationOptions}
        instructors={instructors}
        onSave={saveEdit}
      />

      <PaymentOverrideDialog
        open={overrideDialogOpen}
        onOpenChange={setOverrideDialogOpen}
        overrideType={overrideType}
        noteText={overrideNoteText}
        onNoteTextChange={setOverrideNoteText}
        submitting={overrideSubmitting}
        onSubmit={submitOverride}
      />
    </div>
  );
}
